import { Fragment, useState } from 'react';

import { useDispatch } from 'react-redux';

import Button from 'common-ui/button/Button';
import Modal from 'common-ui/modal';
import {
  createAdministrativeDistrict,
  createAdministrativeRegion,
  createBuilding,
  createCity,
  createCountry,
  createStreet,
  deleteAdministrativeDistrict,
  deleteAdministrativeRegion,
  deleteBuilding,
  deleteCity,
  deleteCountry,
  deleteStreet,
  updateAdministrativeDistrict,
  updateAdministrativeRegion,
  updateBuilding,
  updateCity,
  updateCountry,
  updateStreet,
} from 'features/newsales/newsalesActions';
import {
  setAdministrativeDistrictsCurrentPage,
  setAdministrativeRegionsCurrentPage,
  setBuildingsCurrentPage,
  setCitiesCurrentPage,
  setCountriesCurrentPage,
  setStreetsCurrentPage,
} from 'features/newsales/newsalesSlice';

import {
  AddButtonWrapper,
  AddressesContainer,
  AddressesContent,
  ConfirmMessage,
  ModalContent,
  Tab,
  TabSeparator,
  TabsContainer,
  TabsList,
} from './Addresses.styles';
import CountryModal from './CountryModal';
import BuildingModal from './modals/BuildingModal';
import CityModal from './modals/CityModal';
import DistrictModal from './modals/DistrictModal';
import RegionModal from './modals/RegionModal';
import StreetModal from './modals/StreetModal';
import BuildingPage from './pages/BuildingPage';
import CityVillagePage from './pages/CityVillagePage';
import CountryPage from './pages/CountryPage';
import DistrictPage from './pages/DistrictPage';
import RegionPage from './pages/RegionPage';
import StreetPage from './pages/StreetPage';

const TABS = [
  { id: 'country', label: 'Country', addLabel: 'Add Country' },
  { id: 'region', label: 'Region', addLabel: 'Add Region' },
  { id: 'city', label: 'City / Village', addLabel: 'Add City/Village' },
  { id: 'street', label: 'Street', addLabel: 'Add Street' },
  { id: 'district', label: 'Administrative district', addLabel: 'Add District' },
  { id: 'building', label: 'Building / House', addLabel: 'Add Building/House' },
];

const Addresses = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('country');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  const [allCountries, setAllCountries] = useState([]);
  const [regionsForCities, setRegionsForCities] = useState([]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'country') {
      dispatch(setCountriesCurrentPage(1));
    } else if (tabId === 'region') {
      dispatch(setAdministrativeRegionsCurrentPage(1));
    } else if (tabId === 'city') {
      dispatch(setCitiesCurrentPage(1));
    } else if (tabId === 'district') {
      dispatch(setAdministrativeDistrictsCurrentPage(1));
    } else if (tabId === 'street') {
      dispatch(setStreetsCurrentPage(1));
    } else if (tabId === 'building') {
      dispatch(setBuildingsCurrentPage(1));
    }
  };

  const handleAdd = () => {
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setSelectedStreet(null);
    setSelectedBuilding(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    if (activeTab === 'country') {
      setSelectedCountry(item);
    } else if (activeTab === 'region') {
      setSelectedRegion(item);
    } else if (activeTab === 'city') {
      setSelectedCity(item);
    } else if (activeTab === 'district') {
      setSelectedDistrict(item);
    } else if (activeTab === 'street') {
      setSelectedStreet(item);
    } else if (activeTab === 'building') {
      setSelectedBuilding(item);
    }
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    setDeleteItemId(item.id);
    setDeleteItemName(item.name);
    setIsDeleteModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setSelectedStreet(null);
    setSelectedBuilding(null);
  };

  const handleModalSubmit = (data) => {
    if (activeTab === 'country') {
      if (selectedCountry) {
        dispatch(updateCountry({ id: selectedCountry.id, data }));
      } else {
        dispatch(createCountry(data));
      }
    } else if (activeTab === 'region') {
      if (selectedRegion) {
        dispatch(updateAdministrativeRegion({ id: selectedRegion.id, data }));
      } else {
        dispatch(createAdministrativeRegion(data));
      }
    } else if (activeTab === 'city') {
      if (selectedCity) {
        dispatch(updateCity({ id: selectedCity.id, data }));
      } else {
        dispatch(createCity(data));
      }
    } else if (activeTab === 'district') {
      if (selectedDistrict) {
        dispatch(updateAdministrativeDistrict({ id: selectedDistrict.id, data }));
      } else {
        dispatch(createAdministrativeDistrict(data));
      }
    } else if (activeTab === 'street') {
      if (selectedStreet) {
        dispatch(updateStreet({ id: selectedStreet.id, data }));
      } else {
        dispatch(createStreet(data));
      }
    } else if (activeTab === 'building') {
      if (selectedBuilding) {
        dispatch(updateBuilding({ id: selectedBuilding.id, data }));
      } else {
        dispatch(createBuilding(data));
      }
    }
    handleModalClose();
  };

  const handleDeleteConfirm = () => {
    if (activeTab === 'country') {
      dispatch(deleteCountry(deleteItemId));
    } else if (activeTab === 'region') {
      dispatch(deleteAdministrativeRegion(deleteItemId));
    } else if (activeTab === 'city') {
      dispatch(deleteCity(deleteItemId));
    } else if (activeTab === 'district') {
      dispatch(deleteAdministrativeDistrict(deleteItemId));
    } else if (activeTab === 'street') {
      dispatch(deleteStreet(deleteItemId));
    } else if (activeTab === 'building') {
      dispatch(deleteBuilding(deleteItemId));
    }
    setIsDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemName('');
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemName('');
  };

  const activeTabData = TABS.find((tab) => tab.id === activeTab);

  return (
    <>
      <AddressesContainer>
        <TabsContainer>
          <TabsList>
            {TABS.map((tab, index) => (
              <Fragment key={tab.id}>
                <Tab $active={activeTab === tab.id} onClick={() => handleTabChange(tab.id)}>
                  {tab.label}
                </Tab>
                {index < TABS.length - 1 && (
                  <TabSeparator
                    $active={activeTab === tab.id || activeTab === TABS[index + 1].id}
                  />
                )}
              </Fragment>
            ))}
          </TabsList>
          <AddButtonWrapper>
            <Button secondary onClick={handleAdd}>
              + {activeTabData?.addLabel || 'Add'}
            </Button>
          </AddButtonWrapper>
        </TabsContainer>

        <AddressesContent>
          {activeTab === 'country' && (
            <CountryPage
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAllCountriesChange={setAllCountries}
            />
          )}

          {activeTab === 'region' && (
            <RegionPage
              onEdit={handleEdit}
              onDelete={handleDelete}
              allCountries={allCountries}
            />
          )}

          {activeTab === 'city' && (
            <CityVillagePage
              onEdit={handleEdit}
              onDelete={handleDelete}
              allCountries={allCountries}
              onRegionsChange={setRegionsForCities}
            />
          )}

          {activeTab === 'district' && (
            <DistrictPage
              onEdit={handleEdit}
              onDelete={handleDelete}
              allCountries={allCountries}
            />
          )}

          {activeTab === 'street' && (
            <StreetPage
              onEdit={handleEdit}
              onDelete={handleDelete}
              allCountries={allCountries}
            />
          )}

          {activeTab === 'building' && (
            <BuildingPage
              onEdit={handleEdit}
              onDelete={handleDelete}
              allCountries={allCountries}
            />
          )}
        </AddressesContent>
      </AddressesContainer>

      {activeTab === 'country' && (
        <CountryModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          country={selectedCountry}
          loading={false}
        />
      )}

      {activeTab === 'region' && (
        <RegionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          region={selectedRegion}
          countries={allCountries}
          loading={false}
        />
      )}

      {activeTab === 'city' && (
        <CityModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          city={selectedCity}
          countries={allCountries}
          loading={false}
        />
      )}

      {activeTab === 'district' && (
        <DistrictModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          district={selectedDistrict}
          countries={allCountries}
          loading={false}
        />
      )}

      {activeTab === 'street' && (
        <StreetModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          street={selectedStreet}
          countries={allCountries}
          loading={false}
        />
      )}

      {activeTab === 'building' && (
        <BuildingModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          building={selectedBuilding}
          countries={allCountries}
          loading={false}
        />
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onOk={handleDeleteConfirm}
        title="Confirm Deletion"
        okText="Delete"
        cancelText="Cancel"
        footer
        closeIcon
        width="450px"
      >
        <ModalContent>
          <ConfirmMessage>
            Are you sure you want to delete <strong>{deleteItemName}</strong>?
          </ConfirmMessage>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Addresses;
